import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Heart, Share2, MoreHorizontal, Trash2, MessageCircle } from 'lucide-react';
import HashtagText from './ui/HashtagText';

interface Post {
  _id: string;
  content: string;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  isLiked: boolean;
  commentCount: number;
  deleted?: boolean;
}

interface Comment {
  _id: string;
  content: string;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface PostModalProps {
  post: Post | null;
  isOpen: boolean;
  onClose: () => void;
  onPostUpdate: (updatedPost: Post) => void;
  onPostDelete: (postId: string) => void;
  externalComments?: Comment[];
  onExternalCommentAdded?: (comment: Comment) => void;
  onExternalCommentDeleted?: (commentId: string) => void;
}

const PostModal: React.FC<PostModalProps> = ({ 
  post, 
  isOpen, 
  onClose, 
  onPostUpdate, 
  onPostDelete,
  externalComments = [],
  onExternalCommentAdded,
  onExternalCommentDeleted
}) => {  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Local state to track the current post (updates when prop changes or when we update it)
  const [currentPost, setCurrentPost] = useState<Post | null>(post);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [deletePostDialogOpen, setDeletePostDialogOpen] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Update local post state when prop changes
  useEffect(() => {
    setCurrentPost(post);
  }, [post]);  const fetchComments = useCallback(async () => {
    if (!currentPost) return;

    setIsLoadingComments(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/${currentPost._id}/comments`);
      const data = await response.json();

      if (data.status === 'success') {
        const fetchedComments = data.data.comments || [];
        
        // Merge fetched comments with external comments, avoiding duplicates
        const allComments = [...fetchedComments];
        
        // Add external comments that aren't already in fetched comments
        externalComments.forEach(extComment => {
          if (!fetchedComments.some((comment: Comment) => comment._id === extComment._id)) {
            allComments.push(extComment);
          }
        });
        
        // Sort by creation time (oldest first)
        allComments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        
        setComments(allComments);
      } else {
        console.error('Failed to fetch comments:', data.message);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  }, [currentPost, externalComments]);  // Fetch comments when modal opens
  useEffect(() => {
    if (isOpen && currentPost) {
      fetchComments();
    }
  }, [isOpen, currentPost, fetchComments]);

  // Clear comments when modal closes
  useEffect(() => {
    if (!isOpen) {
      setComments([]);
    }
  }, [isOpen]);

  const openDeleteCommentDialog = (commentId: string) => {
    setCommentToDelete(commentId);
    setDeleteCommentDialogOpen(true);
  };
  const closeDeleteCommentDialog = () => {
    setDeleteCommentDialogOpen(false);
    setCommentToDelete(null);
  };

  const openDeletePostDialog = () => {
    setDeletePostDialogOpen(true);
  };
  const closeDeletePostDialog = () => {
    setDeletePostDialogOpen(false);
  };

  const handleDeletePost = async () => {
    if (!user || !currentPost) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/${currentPost._id}`, {
        method: 'DELETE',
        headers: headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete post');
      }      // Close the modal first
      onClose();
      
      // Close the delete dialog
      closeDeletePostDialog();
      
      // Then call the parent's delete handler to remove from UI
      onPostDelete(currentPost._id);
      
      addToast('Post deleted successfully!', 'success');

    } catch (error) {
      console.error('Error deleting post:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to delete post', 
        'error'
      );
    }
  };

  const handleLikePost = async () => {
    if (!user || !currentPost) {
      addToast('Please log in to like posts', 'error');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const isCurrentlyLiked = currentPost.isLiked;
      const url = `${process.env.REACT_APP_API_URL}/api/posts/${currentPost._id}/like`;
      const method = isCurrentlyLiked ? 'DELETE' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update like status');
      }

      const result = await response.json();

      // Update the post with new like data
      const updatedPost = {
        ...currentPost,
        likeCount: result.data.likeCount,
        isLiked: result.data.isLiked
      };

      // Update both local state and parent state
      setCurrentPost(updatedPost);
      onPostUpdate(updatedPost);

    } catch (error) {
      console.error('Error updating like status:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to update like status', 
        'error'
      );
    }
  };
  const handleSubmitComment = async () => {
    if (!user) {
      addToast('Please log in to comment', 'error');
      return;
    }

    if (!currentPost) return;

    const content = commentInputRef.current?.value?.trim();
    if (!content) {
      addToast('Please enter a comment', 'error');
      return;
    }

    setIsSubmittingComment(true);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/${currentPost._id}/comments`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create comment');
      }

      const result = await response.json();

      // Add new comment to the list
      setComments(prev => [...prev, result.data.comment]);

      // Notify parent component about the new comment
      if (onExternalCommentAdded) {
        onExternalCommentAdded(result.data.comment);
      }

      // Update post comment count
      const updatedPost = {
        ...currentPost,
        commentCount: currentPost.commentCount + 1
      };
      setCurrentPost(updatedPost);
      onPostUpdate(updatedPost);

      // Clear the input
      if (commentInputRef.current) {
        commentInputRef.current.value = '';
      }

      addToast('Comment added successfully!', 'success');

    } catch (error) {
      console.error('Error creating comment:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to create comment', 
        'error'
      );
    } finally {
      setIsSubmittingComment(false);
    }
  };
  const handleDeleteComment = async () => {
    if (!user || !commentToDelete) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/comments/${commentToDelete}`, {
        method: 'DELETE',
        headers: headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete comment');
      }

      // Only after successful deletion from backend:
      // 1. Notify parent to clear external comments
      if (onExternalCommentDeleted) {
        onExternalCommentDeleted(commentToDelete);
      }

      // 2. Refetch comments to get the updated list from server
      await fetchComments();

      // 3. Update post comment count
      if (currentPost) {
        const updatedPost = {
          ...currentPost,
          commentCount: Math.max(0, currentPost.commentCount - 1)
        };
        setCurrentPost(updatedPost);
        onPostUpdate(updatedPost);
      }

      addToast('Comment deleted successfully!', 'success');
      closeDeleteCommentDialog();

    } catch (error) {
      console.error('Error deleting comment:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to delete comment', 
        'error'
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleSharePost = async (event: React.MouseEvent) => {
    event.stopPropagation();
    
    const postUrl = `${window.location.origin}/@${currentPost!.user.handle}/post/${currentPost!._id}`;
    
    try {
      await navigator.clipboard.writeText(postUrl);
      addToast('Post link copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      
      // Fallback: show the URL in a prompt
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
        // On mobile, we'll show a different message
        addToast('Link ready to share: tap and hold to copy', 'info');
      } else {
        addToast('Unable to copy automatically. Please copy manually.', 'error');
      }
    }
  };

  if (!currentPost) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden bg-paper border-parchment-300 text-custom-blue-900 p-0">        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="text-custom-blue-900">Post Details</DialogTitle>
          <DialogDescription className="sr-only">
            View and interact with this post and its comments
          </DialogDescription>
        </DialogHeader>        <div className="flex flex-col max-h-[calc(90vh-80px)] w-full overflow-hidden">          {/* Post Content */}
          <div className="p-4 border-b border-parchment-200 w-full max-w-full overflow-hidden">
            {/* Header: Profile Picture + Profile Info + Dropdown Menu */}
            <div className="flex items-center gap-3 mb-3">
              <Link 
                to={`/@${currentPost.user.handle}`}
                className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                onClick={onClose}
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={currentPost.user.picture} alt={currentPost.user.name} />
                  <AvatarFallback className="bg-parchment-200 text-custom-blue-800">
                    {currentPost.user.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Link>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <Link 
                    to={`/@${currentPost.user.handle}`}
                    className="cursor-pointer hover:text-custom-blue-700 transition-colors"
                    onClick={onClose}
                  >
                    <span className="font-semibold text-custom-blue-900 text-sm">
                      {currentPost.user.name}
                    </span>
                  </Link>
                  <Link 
                    to={`/@${currentPost.user.handle}`}
                    className="cursor-pointer hover:text-custom-blue-500 transition-colors"
                    onClick={onClose}
                  >
                    <span className="text-sm text-custom-blue-600">
                      @{currentPost.user.handle}
                    </span>
                  </Link>
                  <span className="text-sm text-custom-blue-500">•</span>
                  <span className="text-sm text-custom-blue-500">
                    {formatDate(currentPost.createdAt)}
                  </span>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                {user && user.handle === currentPost.user.handle && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100"
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="end" 
                      className="bg-paper border-parchment-300 z-[9999] shadow-lg"
                      sideOffset={5}
                    >
                      <DropdownMenuItem 
                        onClick={openDeletePostDialog}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete this post
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>            {/* Post Content */}
            <div className="ml-12 mb-3 pr-4 max-w-full">
              <div className="w-full max-w-full overflow-hidden" style={{wordBreak: 'break-word', overflowWrap: 'break-word'}}>
                <HashtagText 
                  text={currentPost.content}
                  className="text-sm text-custom-blue-800 leading-relaxed whitespace-pre-wrap break-words word-break-break-word block"
                />
              </div>
            </div>

            {/* Footer: Comment Count + Action Buttons */}
            <div className="flex items-center justify-between ml-12 pr-4">
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-4 h-4 text-custom-blue-600" />
                <span className="text-sm text-custom-blue-700">
                  {currentPost.commentCount} {currentPost.commentCount === 1 ? 'comment' : 'comments'}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {user ? (
                  // Show interactive buttons for authenticated users
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLikePost}
                      className={`h-10 w-10 p-0 hover:bg-parchment-100 transition-colors ${currentPost.isLiked ? 'text-red-500' : 'text-custom-blue-600'}`}
                    >
                      <Heart 
                        className="w-5 h-5" 
                        fill={currentPost.isLiked ? 'currentColor' : 'none'} 
                      />
                    </Button>
                    {currentPost.likeCount > 0 && (
                      <span className="text-sm text-gray-600 ml-1">
                        {currentPost.likeCount}
                      </span>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100 transition-colors"
                      onClick={(e) => handleSharePost(e)}
                    >
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </>
                ) : (
                  // Show static view for non-authenticated users, but make share button functional
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 text-custom-blue-600">
                      <Heart className="w-5 h-5" fill="none" />
                      {currentPost.likeCount > 0 && (
                        <span className="text-sm text-gray-600">
                          {currentPost.likeCount}
                        </span>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100 transition-colors"
                      onClick={(e) => handleSharePost(e)}
                    >
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>          {/* Comments Section */}
          <div className="flex-1 overflow-y-auto p-4 w-full max-w-full overflow-hidden">
            {isLoadingComments ? (
              <div className="text-center text-custom-blue-600 py-4">
                Loading comments...
              </div>
            ) : comments.length > 0 ? (              <div className="space-y-4 w-full max-w-full">
                {comments.map((comment) => (                  <div key={comment._id} className="bg-parchment-50 rounded-lg p-3 min-w-0 max-w-full overflow-hidden">
                    <div className="flex items-start space-x-3 min-w-0 max-w-full overflow-hidden">
                      <Link 
                        to={`/@${comment.user.handle}`}
                        className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                        onClick={onClose}
                      >
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={comment.user.picture} alt={comment.user.name} />
                          <AvatarFallback className="bg-paper text-custom-blue-900 text-xs">
                            {comment.user.name.substring(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2 min-w-0 overflow-hidden">
                            <Link 
                              to={`/@${comment.user.handle}`}
                              className="cursor-pointer hover:text-custom-blue-700 transition-colors"
                              onClick={onClose}
                            >
                              <span className="text-sm font-semibold text-custom-blue-900 truncate">
                                {comment.user.name}
                              </span>
                            </Link>
                            <Link 
                              to={`/@${comment.user.handle}`}
                              className="cursor-pointer hover:text-custom-blue-500 transition-colors"
                              onClick={onClose}
                            >
                              <span className="text-xs text-custom-blue-600 truncate">
                                @{comment.user.handle}
                              </span>
                            </Link>
                            <span className="text-xs text-custom-blue-500 flex-shrink-0">
                              {formatDate(comment.createdAt)}
                            </span>
                          </div>
                          {user && user.handle === comment.user.handle && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteCommentDialog(comment._id)}
                              className="h-10 w-10 p-2 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex-shrink-0"
                              title="Delete comment"
                            >
                              <Trash2 className="h-5 w-5" />
                            </Button>
                          )}
                        </div>                        <div className="w-full max-w-full overflow-hidden">
                          <p className="text-sm text-custom-blue-800 whitespace-pre-wrap break-words" style={{wordBreak: 'break-word', overflowWrap: 'break-word'}}>
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-custom-blue-600 py-8">
                <p>No comments yet.</p>
                <p className="text-sm mt-1">Be the first to share your thoughts!</p>
              </div>
            )}
          </div>

          {/* Comment Input */}
          {user ? (
            <div className="p-4 border-t border-parchment-200">
              <div className="flex space-x-3">
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-paper text-custom-blue-900 text-xs">
                    {user.name.substring(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <textarea
                    ref={commentInputRef}
                    placeholder="Write a comment..."
                    maxLength={200}
                    className="w-full min-h-[80px] p-3 rounded-lg border border-parchment-300 bg-white text-sm text-custom-blue-900 placeholder-custom-blue-500 focus:outline-none focus:ring-2 focus:ring-custom-blue-400 resize-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-custom-blue-500">
                      Press Enter to post, Shift+Enter for new line
                    </span>
                    <Button
                      onClick={handleSubmitComment}
                      disabled={isSubmittingComment}
                      className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white"
                    >
                      {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t border-parchment-200 text-center">
              <p className="text-custom-blue-600 mb-3">Join the conversation!</p>
              <Link 
                to="/signin"
                className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white px-4 py-2 rounded-md transition-colors font-medium"
              >
                Sign In to Comment
              </Link>
            </div>
          )}
        </div>        {/* Delete Comment Confirmation Dialog */}
        <Dialog open={deleteCommentDialogOpen} onOpenChange={setDeleteCommentDialogOpen}>
          <DialogContent className="bg-paper border-parchment-300 text-custom-blue-900">
            <DialogHeader>
              <DialogTitle className="text-custom-blue-900">Delete Comment</DialogTitle>
              <DialogDescription className="text-custom-blue-700">
                Are you sure you want to delete this comment? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={closeDeleteCommentDialog}
                className="border-parchment-300 text-custom-blue-700 hover:bg-parchment-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteComment}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Post Confirmation Dialog */}
        <Dialog open={deletePostDialogOpen} onOpenChange={setDeletePostDialogOpen}>
          <DialogContent className="bg-paper border-parchment-300 text-custom-blue-900">
            <DialogHeader>
              <DialogTitle className="text-custom-blue-900">Delete Post</DialogTitle>
              <DialogDescription className="text-custom-blue-700">
                Are you sure you want to delete this post? This action cannot be undone and will also delete all comments on this post.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={closeDeletePostDialog}
                className="border-parchment-300 text-custom-blue-700 hover:bg-parchment-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeletePost}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Post
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default PostModal;
